import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function VancouverAsianCuisineGuide() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "vancouver-asian-cuisine-guide";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Sun Sui Wah Seafood Restaurant (Chinese)",
    "section-2": "Miku Restaurant (Japanese)",
    "section-3": "Sura Korean Cuisine (Korean)",
    "section-4": "Maenam (Thai)",
    "section-5": "Phnom Penh (Vietnamese/Cambodian)",
    "section-6": "Vij’s (Indian)",
    "section-7": "Banana Leaf (Malaysian)",
    "local-regulations": "Local Dining Regulations",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.destinationvancouver.com",
      label: "Destination Vancouver (Official Tourism Site)",
    },
    {
      url: "https://www.michelin.com",
      label: "Michelin Guide",
    },
    {
      url: "https://www.bclaws.gov.bc.ca",
      label: "BC Laws & Regulations",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // IDs match the section references ("section-1", "section-2", etc.)
  // lat & lng must match the conversation's data, at least 4 decimals
  const locations = [
    {
      id: "section-1",
      lat: 49.2504,
      lng: -123.1007,
      popupText: "Sun Sui Wah Seafood Restaurant",
    },
    {
      id: "section-2",
      lat: 49.2869,
      lng: -123.1125,
      popupText: "Miku Restaurant",
    },
    {
      id: "section-3",
      lat: 49.2881,
      lng: -123.1333,
      popupText: "Sura Korean Cuisine",
    },
    {
      id: "section-4",
      lat: 49.2679,
      lng: -123.1489,
      popupText: "Maenam",
    },
    {
      id: "section-5",
      lat: 49.2784,
      lng: -123.0982,
      popupText: "Phnom Penh",
    },
    {
      id: "section-6",
      lat: 49.2580,
      lng: -123.1140,
      popupText: "Vij’s",
    },
    {
      id: "section-7",
      lat: 49.2890,
      lng: -123.1399,
      popupText: "Banana Leaf",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Authentic Asian Cuisine Hotspots in Vancouver</title>
        <meta
          name="description"
          content="A thorough guide to Vancouver's top authentic Asian cuisine destinations, including addresses, operating hours, and valuable tips."
        />
        <meta
          name="keywords"
          content="Vancouver, Asian Cuisine, Food, Restaurants"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/vancouver-asian-cuisine-guide.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Authentic Asian Cuisine Hotspots in Vancouver"
        />
        <meta
          property="og:description"
          content="Explore Vancouver's best Asian restaurants, from dim sum to aburi sushi, including local tips and addresses."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/vancouver-asian-cuisine-guide.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/vancouver-asian-cuisine-guide.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Authentic Asian Cuisine Hotspots in Vancouver"
        />
        <meta
          name="twitter:description"
          content="Discover top spots in Vancouver for Chinese, Japanese, Korean, Thai, and more."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/vancouver-asian-cuisine-guide.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="A variety of Asian dishes on a table"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver is renowned for its diverse and <strong>authentic Asian cuisine</strong>, 
              from sizzling street-style bites to refined fine dining. This guide highlights 
              some of the best Asian dining hotspots the city has to offer, spanning Chinese, 
              Japanese, Korean, Thai, Vietnamese, Indian, and Malaysian restaurants. Each listing 
              includes addresses, operating hours, approximate costs, and coordinates, making it 
              easy for you to navigate and savor your next culinary adventure.
            </p>

            {/* SECTION 1: Sun Sui Wah (Chinese) */}
            <h2 id="section-1">Sun Sui Wah Seafood Restaurant (Chinese)</h2>
            <p
              ref={(el) => setSpotRef("section-1", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2504}
                coordX={-123.1007}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;3888 Main Street, Vancouver, BC V5V 3N9
            </p>
            <p>
              Sun Sui Wah is a long-standing Cantonese restaurant renowned for its fresh seafood 
              and classic dim sum. The spacious dining hall is perfect for weekend brunch or big 
              family gatherings. Try their signature roasted squab and steamed shrimp dumplings. 
              <em>Pro tip</em>: Arrive early on weekends to secure a table for dim sum and avoid 
              the crowds.
            </p>
            <ul>
              <li>
                <strong>Hours:</strong> Mon–Fri 10:30am–3:00pm (dim sum), 5:00pm–10:00pm (dinner); 
                Sat–Sun 10:00am–3:00pm (dim sum), 5:00pm–10:00pm
              </li>
              <li>
                <strong>Approx. Cost:</strong> $$–$$$ 
              </li>
            </ul>

            {/* SECTION 2: Miku (Japanese) */}
            <h2 id="section-2">Miku Restaurant (Japanese)</h2>
            <p
              ref={(el) => setSpotRef("section-2", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2869}
                coordX={-123.1125}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;#70-200 Granville Street, Vancouver, BC V6C 1S4
            </p>
            <p>
              Miku is an upscale Japanese restaurant by the waterfront, famous for its 
              <em>aburi</em> (flame-seared) sushi and breathtaking views. Their Aburi Salmon Oshi 
              Sushi is a must-try for first-time visitors. While it’s on the pricier side, the 
              fine-dining ambiance and top-notch seafood justify the cost. 
            </p>
            <ul>
              <li>
                <strong>Hours:</strong> Daily 11:30am–10:00pm
              </li>
              <li>
                <strong>Approx. Cost:</strong> $$$–$$$$
              </li>
            </ul>

            {/* SECTION 3: Sura (Korean) */}
            <h2 id="section-3">Sura Korean Cuisine (Korean)</h2>
            <p
              ref={(el) => setSpotRef("section-3", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2881}
                coordX={-123.1333}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;1518 Robson Street, Vancouver, BC V6G 1C3
            </p>
            <p>
              Sura brings royal court-inspired dishes to downtown Vancouver. It's known for its 
              bulgogi, bibimbap, and sizzling BBQ platters. Crowds often line up for lunch specials, 
              so plan accordingly. Their crispy potato pancakes and generous side dishes make Sura 
              a favorite among locals.
            </p>
            <ul>
              <li>
                <strong>Hours:</strong> Lunch 11:30am–3:00pm; Dinner 5:00pm–9:30pm 
                (till 10:00pm Fri/Sat)
              </li>
              <li>
                <strong>Approx. Cost:</strong> $$
              </li>
            </ul>

            {/* SECTION 4: Maenam (Thai) */}
            <h2 id="section-4">Maenam (Thai)</h2>
            <p
              ref={(el) => setSpotRef("section-4", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2679}
                coordX={-123.1489}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;1938 West 4th Avenue, Vancouver, BC V6J 1M5
            </p>
            <p>
              Maenam expertly fuses traditional Thai recipes with Pacific Northwest ingredients. 
              From rich, spicy curries to fresh papaya salads, the focus is on balanced flavors. 
              Family-style dining is encouraged; opt for their tasting menu for a curated 
              experience. Reservation is recommended, especially on weekends.
            </p>
            <ul>
              <li>
                <strong>Hours:</strong> Tue–Sun 5:00pm–10:00pm; Wed–Fri lunch 12:00pm–2:00pm; 
                closed Monday
              </li>
              <li>
                <strong>Approx. Cost:</strong> $$$
              </li>
            </ul>

            {/* SECTION 5: Phnom Penh (Vietnamese/Cambodian) */}
            <h2 id="section-5">Phnom Penh (Vietnamese/Cambodian)</h2>
            <p
              ref={(el) => setSpotRef("section-5", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2784}
                coordX={-123.0982}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;244 East Georgia Street, Vancouver, BC V6A 1Z7
            </p>
            <p>
              Tucked in Chinatown, Phnom Penh is a family-run institution famous for its crispy 
              buttered chicken wings and luc lac beef. Expect lines at almost any time of day, 
              especially weekends. The bustling, casual atmosphere pairs perfectly with the 
              rich, flavorful dishes.
            </p>
            <ul>
              <li>
                <strong>Hours:</strong> Mon, Wed–Sun 11:00am–8:00pm (till 9:00pm Fri–Sun); 
                closed Tuesday
              </li>
              <li>
                <strong>Approx. Cost:</strong> $$
              </li>
            </ul>

            {/* SECTION 6: Vij's (Indian) */}
            <h2 id="section-6">Vij’s (Indian)</h2>
            <p
              ref={(el) => setSpotRef("section-6", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2580}
                coordX={-123.1140}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;3106 Cambie Street, Vancouver, BC V5Z 2W2
            </p>
            <p>
              Opened by Chef Vikram Vij in 1994, Vij’s is an iconic Indian restaurant that offers 
              inventive takes on traditional dishes. Its lamb popsicles in fenugreek cream curry 
              are legendary. The atmosphere is upscale yet approachable, and the restaurant often 
              garners rave reviews (including Michelin Bib Gourmand mentions).
            </p>
            <ul>
              <li>
                <strong>Hours:</strong> Dinner daily from 5:30pm; no lunch service
              </li>
              <li>
                <strong>Approx. Cost:</strong> $$–$$$
              </li>
            </ul>

            {/* SECTION 7: Banana Leaf (Malaysian) */}
            <h2 id="section-7">Banana Leaf (Malaysian)</h2>
            <p
              ref={(el) => setSpotRef("section-7", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2890}
                coordX={-123.1399}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;1779 Robson Street, Vancouver, BC V6G 1C9
            </p>
            <p>
              Banana Leaf brings the vibrant flavors of Malaysia to Vancouver, with plenty of 
              curries, noodles, and satays. The casual yet festive interior sets the mood for 
              group meals or solo explorations. It’s a wonderful spot to discover the varied 
              culinary influences that shape Malaysian cuisine, from Thai and Indian to Chinese.
            </p>
            <ul>
              <li>
                <strong>Hours:</strong> Sun–Wed 11:30am–10:30pm; Thu–Sat 11:30am–11:00pm
              </li>
              <li>
                <strong>Approx. Cost:</strong> $$
              </li>
            </ul>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Dining Regulations</h2>
            <p>
              <strong>Tipping:</strong> Vancouver follows standard Canadian tipping norms: 
              around 15–20% on the pre-tax total. Some restaurants automatically add gratuity 
              for large groups. <br />
              <strong>Taxes:</strong> A 5% federal GST is typically applied at restaurants. <br />
              <strong>Reservations & Wait Times:</strong> For popular spots (e.g., Miku, Vij’s), 
              book in advance. Casual places like Phnom Penh don’t accept reservations, so you 
              may need to line up. <br />
              <strong>Dress Code:</strong> Generally casual or smart casual. <br />
              <strong>Payment Methods:</strong> Most places accept credit/debit cards. Cash tips 
              are common but optional. <br />
              <strong>Alcohol & Age:</strong> Legal drinking age is 19 in BC. ID may be required.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Do I need to make reservations for all restaurants?</h3>
              <p>
                Only some restaurants (like Miku or Vij’s) strongly recommend 
                reservations, especially for dinner. Others, like Phnom Penh, operate on 
                a first-come basis. If in doubt, check the restaurant’s website.
              </p>

              <h3>2. What if I have dietary restrictions?</h3>
              <p>
                Vancouver restaurants are generally accommodating. From vegetarian 
                curries at Vij’s to modified dishes at Maenam, it’s best to inform your 
                server. Many menus mark vegetarian/gluten-free options clearly.
              </p>

              <h3>3. Can I use public transportation to reach these places?</h3>
              <p>
                Absolutely. Vancouver is transit-friendly. For instance, Miku is near 
                Waterfront Station, while Phnom Penh is close to Stadium–Chinatown 
                SkyTrain Station. Buses and SkyTrain cover most restaurant areas. 
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              From delicate flame-seared sushi to fragrant Thai curries, Vancouver’s 
              Asian dining scene is as diverse as it is flavorful. Each location 
              highlighted here reflects the city’s multicultural heritage, offering a 
              unique culinary adventure. Whether you’re lining up for dim sum or 
              indulging in Michelin-recommended dishes, you’ll find satisfaction in 
              every bite. 
            </p>
            <p>
              <strong>Disclaimer:</strong> The information provided (such as hours or 
              prices) is accurate at the time of writing. Always double-check with 
              official resources or the restaurant’s website for the latest updates. 
              Please follow local safety guidelines and enjoy your food journey 
              responsibly.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay ref={mobileMapRef} markers={locations} spotRefs={spotRefs} />

      <Footer />
    </>
  );
}